import { TweenLite } from 'gsap';
import { filter, find, last, sortBy } from 'lodash';
import React, { useContext, useEffect, useRef } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { useOnMount } from '../../../../../helpers/hooks/useOnMount';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { Expandable } from '../../../../../shared/Expandable';
import { FormFieldRow } from '../../../../../shared/form/FormFieldRow';
import { VerticalCheckboxField } from '../../../../../shared/form/inputs/CheckboxField';
import { SelectOptions } from '../../../../../shared/form/inputs/Select';
import { SelectField } from '../../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { spacing04 } from '../../../../../styling/spacing';
import { DesignMaterialsCalculationsRequest } from '../../../../designs/design-form/calculations/materials/designMaterialsCalculationsResponse';
import { AnimateInContext } from '../../AnimateInContext';
import { PumpFormModel } from '../../GetDataForQuoteOrDesignForm';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { manifoldFieldName } from '../../quoteOrDesignFormFieldNameHelpers';
import { UnitFieldContext } from '../../units/UnitFieldContext';
import { ManifoldFormModel, manifoldHasSkirtingHeating } from '../manifoldFormModel';
import { RoomlessManifoldForm } from '../RoomlessManifoldForm';
import { ManifoldFieldsHeader } from './ManifoldFieldsHeader';
import { ManifoldRoomsField } from './ManifoldRoomsField';

type Props = {
  manifold: ManifoldFormModel;
  manifoldIndex: number;
  isExpanded: boolean;
  dragHandleProps: DraggableProvidedDragHandleProps | null;
  deleteManifold: () => void;
  duplicateManifold: () => void;
  pumpOnManifold: boolean;
  designMaterialsCalculationsRequest?: DesignMaterialsCalculationsRequest;
};

export const ManifoldFields = ({
  manifold,
  manifoldIndex,
  isExpanded,
  dragHandleProps,
  deleteManifold,
  duplicateManifold,
  pumpOnManifold,
  designMaterialsCalculationsRequest,
}: Props) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { formType, buildTypeCode, formProps } = formContext;
  const { setFieldValue } = formProps;

  const { manifoldPumpOptions, manifoldFloorConstructionOptions } = formContext.referenceData;

  const unitFieldContext = assertNotNull(useContext(UnitFieldContext));
  const { unit, unitIndex } = unitFieldContext;

  const container = useRef<HTMLDivElement | null>(null);
  const animateInContext = useContext(AnimateInContext);

  useOnMount(() => {
    if (!animateInContext.hasAnimated(manifold.id)) {
      if (container.current != null) {
        TweenLite.set(container.current, { opacity: 1, backgroundColor: 'transparent' });
        TweenLite.from(container.current, 2, { opacity: 0, backgroundColor: 'white' });
      }
      animateInContext.setAnimated(manifold.id);
    }
  });

  const totalUnderfloorHeatingAreaInMetresSquared = Number(manifold.totalUnderfloorHeatingAreaInMetresSquared);

  const hasSkirtingHeating = manifoldHasSkirtingHeating(manifold);
  // When the pump selection type is 'Automatic' we automatically choose the most appropriate value for the
  // 'pump' field based on the total area of the Manifold and whether it contains skirting heating rooms
  useEffect(() => {
    if (
      isNaN(totalUnderfloorHeatingAreaInMetresSquared) ||
      unit.hasPipelineMap ||
      unit.pumpSelectionType !== 'Automatic'
    ) {
      return;
    }

    if (unit.pumpLocation !== 'PumpOnManifold') {
      setFieldValue(manifoldFieldName(unitIndex, manifoldIndex, 'pump'), null);
      return;
    }

    if (hasSkirtingHeating) {
      setFieldValue(manifoldFieldName(unitIndex, manifoldIndex, 'pump'), null);
      return;
    }

    const nonNullManifoldPumpOptions = filter(manifoldPumpOptions, option => option.value != null) as SelectOptions<
      PumpFormModel
    >;

    const manifoldPumpOptionsFromSmallestToLargest = sortBy(
      nonNullManifoldPumpOptions,
      option => option.value.maxAreaInMetresSquared,
    );

    const bestPump =
      (
        find(
          manifoldPumpOptionsFromSmallestToLargest,
          option => option.value.maxAreaInMetresSquared >= totalUnderfloorHeatingAreaInMetresSquared,
        ) ?? last(manifoldPumpOptionsFromSmallestToLargest)
      )?.value ?? null;

    setFieldValue(manifoldFieldName(unitIndex, manifoldIndex, 'pump'), bestPump);
  }, [totalUnderfloorHeatingAreaInMetresSquared, unit.pumpLocation, unit.pumpSelectionType, hasSkirtingHeating]);

  return (
    <div ref={container} style={{ padding: `${spacing04}` }}>
      <ManifoldFieldsHeader
        unitIndex={unitIndex}
        unit={unit}
        manifoldIndex={manifoldIndex}
        manifold={manifold}
        isExpanded={isExpanded}
        dragHandleProps={dragHandleProps}
        deleteManifold={deleteManifold}
        duplicateManifold={duplicateManifold}
      />
      <FormFieldRow type="fill">
        {(formType !== 'Design' || !unit.hasPipelineMap) &&
          pumpOnManifold &&
          !manifold.isRadiator &&
          unit.pumpSelectionType !== 'PumpsNotSupplied' && (
            <SelectField
              name={manifoldFieldName(unitIndex, manifoldIndex, 'pump')}
              label="Pump"
              disabled={unit.pumpSelectionType === 'Automatic'}
              options={manifoldPumpOptions}
            />
          )}
        {manifold.designManifoldDetails != null && (
          <>
            <TextInputField
              name={manifoldFieldName(
                unitIndex,
                manifoldIndex,
                'designManifoldDetails.flowTemperatureInDegreesCelsius',
              )}
              label="Flow Temperature (°C)"
            />
            <VerticalCheckboxField
              name={manifoldFieldName(
                unitIndex,
                manifoldIndex,
                'designManifoldDetails.disableNumberOfPortsAndPipesPerPortCalculation',
              )}
              label="Ports/Pipes Calculation Disabled?"
            />
            <VerticalCheckboxField
              name={manifoldFieldName(unitIndex, manifoldIndex, 'designManifoldDetails.isMountedUpsideDown')}
              label="Mount Upside-Down?"
            />
          </>
        )}
      </FormFieldRow>
      <Expandable isExpanded={isExpanded}>
        {manifold.rooms != null ? (
          <ManifoldRoomsField
            formType={formType}
            buildTypeCode={buildTypeCode}
            manifold={manifold}
            unit={unit}
            unitIndex={unitIndex}
            manifoldIndex={manifoldIndex}
            designMaterialsCalculationsRequest={designMaterialsCalculationsRequest}
          />
        ) : manifold.roomlessManifold != null ? (
          <RoomlessManifoldForm
            roomlessManifold={manifold.roomlessManifold}
            unitIndex={unitIndex}
            manifoldIndex={manifoldIndex}
            floorConstructions={manifoldFloorConstructionOptions}
          />
        ) : null}
      </Expandable>
    </div>
  );
};
